import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, Plus, Edit2, Trash2, Users, Clock, MapPin, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import ScheduleEventModal from './ScheduleEventModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  Scheduled: 'bg-emerald-100 text-emerald-700',
  Completed: 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-gray-200 text-gray-600 line-through',
};

function formatLocal(startAt, durationMin) {
  if (!startAt) return '';
  try {
    // startAt is a naive local ISO ("2026-05-10T14:30")
    const dt = new Date(startAt);
    if (isNaN(dt.getTime())) return startAt;
    const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
    return `${dt.toLocaleString(undefined, opts)} · ${durationMin}m`;
  } catch (_) { return startAt; }
}

export default function ScheduleSection({ relatedType, relatedId, defaultAttendee, title = 'Schedule' }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const fetchEvents = async () => {
    if (!relatedType || !relatedId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events`, { params: { related_type: relatedType, related_id: relatedId } });
      setEvents(res.data || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, [relatedType, relatedId]);

  const cancel = async (ev) => {
    if (!window.confirm(`Cancel "${ev.title}"? Attendees will be notified.`)) return;
    try {
      await axios.put(`${API}/events/${ev.id}`, { status: 'Cancelled', send_invites: true });
      toast.success('Event cancelled, attendees notified');
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Permanently delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, { params: { send_cancellation: false } });
      toast.success('Event deleted');
      fetchEvents();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const now = Date.now();
  const upcoming = events.filter(e => {
    if (e.status === 'Cancelled') return false;
    try { return new Date(e.start_at).getTime() >= now - 60 * 60 * 1000; } catch (_) { return true; }
  });
  const past = events.filter(e => !upcoming.includes(e));

  const visible = showPast ? events : upcoming;

  return (
    <>
      <Card className="p-6" data-testid="schedule-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium flex items-center gap-2"><Calendar size={16} /> {title} ({upcoming.length} upcoming)</h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowPast(!showPast)} data-testid="toggle-past-events">
              {showPast ? 'Hide past/cancelled' : `Show all (${events.length})`}
            </Button>
            <Button size="sm" onClick={() => { setEditingEvent(null); setShowModal(true); }} data-testid="schedule-event-btn">
              <Plus size={13} className="mr-1" /> Schedule
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {showPast ? 'No events.' : 'No upcoming events. Click Schedule to add one.'}
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map(ev => (
              <div key={ev.id} className="border rounded-lg p-3 hover:bg-muted/20" data-testid={`event-row-${ev.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[ev.status] || 'bg-gray-100'}`}>{ev.status}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{ev.type}</span>
                      <span className="font-semibold">{ev.title}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={11} /> {formatLocal(ev.start_at, ev.duration_minutes)}</span>
                      {ev.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={11} />
                          {(ev.location.startsWith('http://') || ev.location.startsWith('https://')) ? (
                            <a href={ev.location} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Join link</a>
                          ) : ev.location}
                        </span>
                      )}
                      {(ev.attendees && ev.attendees.length > 0) && (
                        <span className="flex items-center gap-1"><Users size={11} /> {ev.attendees.length} attendee{ev.attendees.length === 1 ? '' : 's'}</span>
                      )}
                    </div>
                    {ev.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{ev.description}</p>}
                    {(ev.attendees && ev.attendees.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ev.attendees.map((a, i) => (
                          <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100">
                            {a.name}{a.email ? ` <${a.email}>` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {ev.status !== 'Cancelled' && (
                      <Button size="sm" variant="ghost" onClick={() => { setEditingEvent(ev); setShowModal(true); }} data-testid={`edit-event-${ev.id}`}>
                        <Edit2 size={13} />
                      </Button>
                    )}
                    {ev.status === 'Scheduled' && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(ev)} data-testid={`cancel-event-${ev.id}`} title="Cancel & notify attendees">
                        <XCircle size={13} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(ev)} data-testid={`delete-event-${ev.id}`} title="Delete event">
                      <Trash2 size={13} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ScheduleEventModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingEvent(null); }}
        relatedType={relatedType}
        relatedId={relatedId}
        defaultAttendee={defaultAttendee}
        initialEvent={editingEvent}
        onSaved={fetchEvents}
      />
    </>
  );
}
